/**
 * Feature Toggle System for Frontend
 * 
 * This module provides a centralized way to manage feature toggles
 * in the React Native/Expo frontend application.
 * 
 * Feature toggles are controlled via environment variables and API responses.
 */

import React from 'react';

export interface FeatureToggles {
  GPS_TRACKING_ENABLED: boolean;
  GPS_TRACKING_DEV_MODE: boolean;
}

// Default feature toggle values
const DEFAULT_FEATURE_TOGGLES: FeatureToggles = {
  GPS_TRACKING_ENABLED: false,
  GPS_TRACKING_DEV_MODE: false,
};

// Cache for feature toggles
let featureTogglesCache: FeatureToggles | null = null;
let lastFetchTime: number = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Get feature toggles from environment or API
 */
export const getFeatureToggles = async (): Promise<FeatureToggles> => {
  const now = Date.now();
  
  // Return cached value if still valid
  if (featureTogglesCache && (now - lastFetchTime) < CACHE_DURATION) {
    return featureTogglesCache;
  }
  
  try {
    // Try to get from environment first (for development)
    const envToggles = getEnvironmentToggles();
    if (envToggles) {
      featureTogglesCache = envToggles;
      lastFetchTime = now;
      return featureTogglesCache;
    }
    
    // Fallback to API call
    const apiToggles = await fetchFeatureTogglesFromAPI();
    featureTogglesCache = apiToggles;
    lastFetchTime = now;
    return apiToggles;
    
  } catch (error) {
    console.warn('Failed to fetch feature toggles, using defaults:', error);
    featureTogglesCache = DEFAULT_FEATURE_TOGGLES;
    lastFetchTime = now;
    return DEFAULT_FEATURE_TOGGLES;
  }
};

/**
 * Get feature toggles from environment variables
 */
const getEnvironmentToggles = (): FeatureToggles | null => {
  try {
    // In Expo, environment variables are available at build time
    const gpsEnabled = process.env.EXPO_PUBLIC_GPS_TRACKING_ENABLED === 'true';
    const gpsDevMode = process.env.EXPO_PUBLIC_GPS_TRACKING_DEV_MODE === 'true';

    if (gpsEnabled !== undefined || gpsDevMode !== undefined) {
      return {
        GPS_TRACKING_ENABLED: gpsEnabled,
        GPS_TRACKING_DEV_MODE: gpsDevMode,
      };
    }
    
    return null;
  } catch (error) {
    console.warn('Error reading environment toggles:', error);
    return null;
  }
};

/**
 * Fetch feature toggles from API
 */
const fetchFeatureTogglesFromAPI = async (): Promise<FeatureToggles> => {
  try {
    // This would be an API endpoint that returns feature toggles
    // For now, return defaults
    return DEFAULT_FEATURE_TOGGLES;
  } catch (error) {
    console.warn('Error fetching feature toggles from API:', error);
    return DEFAULT_FEATURE_TOGGLES;
  }
};

/**
 * Check if a specific feature is enabled
 */
export const isFeatureEnabled = async (featureName: keyof FeatureToggles): Promise<boolean> => {
  const toggles = await getFeatureToggles();
  return toggles[featureName] || false;
};

/**
 * Check if GPS tracking is enabled
 */
export const isGpsTrackingEnabled = async (): Promise<boolean> => {
  return isFeatureEnabled('GPS_TRACKING_ENABLED');
};

// Synchronous read for render-path gates that cannot tolerate async flicker.
// Backed exclusively by the EXPO_PUBLIC env var so it is resolved at bundle time.
// Default is OFF so the legacy monolithic dashboard render remains active until
// the flag is explicitly enabled (rollback = unset the env var and rebuild).
export const isDriverHomeScreenEnabled = (): boolean => {
  return process.env.EXPO_PUBLIC_DRIVER_HOME_SCREEN_ENABLED === 'true';
};

// Mirrors the driver pattern: owner branch extracted to OwnerHomeScreen behind flag.
// OFF by default; flip EXPO_PUBLIC_OWNER_HOME_SCREEN_ENABLED to 'true' to activate.
export const isOwnerHomeScreenEnabled = (): boolean => {
  return process.env.EXPO_PUBLIC_OWNER_HOME_SCREEN_ENABLED === 'true';
};

// Saved Hours/odometer corrections from the reports (D-021..D-024) are not implemented yet: the backend answers
// 405 to every timelog update, so the report "Edit" buttons stay hidden until that delivery ships.
export const isReportRecordEditingEnabled = (): boolean => false;

/**
 * Check if GPS tracking is in development mode
 */
export const isGpsTrackingDevMode = async (): Promise<boolean> => {
  return isFeatureEnabled('GPS_TRACKING_DEV_MODE');
};

/**
 * Clear feature toggle cache
 */
export const clearFeatureToggleCache = (): void => {
  featureTogglesCache = null;
  lastFetchTime = 0;
};

/**
 * Hook for React components to use feature toggles
 */
export const useFeatureToggle = (featureName: keyof FeatureToggles) => {
  const [isEnabled, setIsEnabled] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(true);
  
  React.useEffect(() => {
    const checkFeature = async () => {
      try {
        setLoading(true);
        const enabled = await isFeatureEnabled(featureName);
        setIsEnabled(enabled);
      } catch (error) {
        console.warn(`Error checking feature ${featureName}:`, error);
        setIsEnabled(false);
      } finally {
        setLoading(false);
      }
    };
    
    checkFeature();
  }, [featureName]);
  
  return { isEnabled, loading };
};

/**
 * Hook for GPS tracking feature
 */
export const useGpsTracking = () => {
  return useFeatureToggle('GPS_TRACKING_ENABLED');
};

/**
 * Component wrapper for feature-gated content
 */
interface FeatureGateProps {
  feature: keyof FeatureToggles;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export const FeatureGate: React.FC<FeatureGateProps> = ({ 
  feature, 
  children, 
  fallback = null 
}) => {
  const { isEnabled, loading } = useFeatureToggle(feature);
  
  if (loading) {
    return null; // or a loading spinner
  }
  
  return isEnabled ? <>{children}</> : <>{fallback}</>;
};

/**
 * GPS Tracking Feature Gate
 */
export const GpsTrackingGate: React.FC<{ children: React.ReactNode; fallback?: React.ReactNode }> = ({ 
  children, 
  fallback = null 
}) => {
  return (
    <FeatureGate feature="GPS_TRACKING_ENABLED" fallback={fallback}>
      {children}
    </FeatureGate>
  );
};
